import React from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Shield, Clock, DollarSign, Users, AlertTriangle, Ban,
  Gavel, Lock, CheckCircle2, XCircle, ArrowRight
} from 'lucide-react';

const RULES_SECTIONS = [
  {
    id: 'age',
    icon: Shield,
    title: '18+ Only – Strict Policy',
    color: '#D4AF37',
    content: [
      'You must be 18 years or older to use KrazyBets.',
      'Identity verification (KYC / FICA compliance) is mandatory.',
      'Fake accounts, duplicate accounts, or identity fraud will result in:'
    ],
    list: [
      'Immediate suspension',
      'Possible permanent ban',
      'Forfeiture of funds'
    ],
    cta: 'Verify Your Account'
  },
  {
    id: 'time',
    icon: Clock,
    title: '48-Hour Bet Matching Rule',
    color: '#28A745',
    content: [
      'Every bet must be accepted (matched) within 48 hours.',
      'If not matched:'
    ],
    list: [
      'Free users: 10% fee, 90% refunded.',
      'Subscribers: Reduced fee (1%-3%), majority refunded.'
    ],
    note: 'Important: Once matched, funds are locked in secure escrow until the result is verified.',
    cta: 'Create a Bet'
  },
  {
    id: 'fees',
    icon: DollarSign,
    title: 'Know Your Costs',
    color: '#6366F1',
    content: ['Full transparency on all fees:'],
    plans: [
      {
        name: 'Free Plan',
        price: '$0',
        features: [
          'Limited access',
          '1 bet per day',
          '10% fee on winnings',
          '10% fee on unmatched bets'
        ]
      },
      {
        name: '$4.99 Plan',
        price: '$4.99/mo',
        features: [
          'Up to 5 bets per day',
          '5% fee on winnings',
          '3% unmatched bet fee',
          'Increased visibility'
        ]
      },
      {
        name: '$9.99 Plan',
        price: '$9.99/mo',
        features: [
          'Unlimited bets',
          '3% fee on winnings',
          '1% unmatched bet fee',
          'Priority exposure',
          'Ability to earn as a moderator (1% share)'
        ]
      }
    ],
    cta: 'Upgrade Your Plan'
  },
  {
    id: 'moderation',
    icon: Users,
    title: 'How We Keep It Fair',
    color: '#17A2B8',
    content: [
      'KrazyBets uses a hybrid moderation system:'
    ],
    list: [
      'AI detects suspicious or harmful bets instantly.',
      'Human moderators review and approve bets.',
      'All bets are vetted before going live.'
    ],
    benefits: [
      'Prevents scams',
      'Ensures fair outcomes',
      'Protects users globally'
    ],
    cta: 'Learn How Moderation Works'
  },
  {
    id: 'prohibited',
    icon: Ban,
    title: 'What You Cannot Bet On',
    color: '#DC3545',
    content: [
      'The following bets are strictly forbidden (ZERO TOLERANCE):'
    ],
    list: [
      'Any form of killing, violence, or harm',
      'Sexual content, pornography, or perversion',
      'Any involvement of minors (under 18)',
      'Prostitution or exploitation',
      'Animal cruelty or killing',
      'Illegal activities or criminal acts',
      'Hate speech, racism, or derogatory bets',
      'Anything deemed offensive, immoral, harmful, or legally questionable'
    ],
    note: 'All content is reviewed by moderators and legal experts.'
  },
  {
    id: 'enforcement',
    icon: Gavel,
    title: 'What Happens If You Break the Rules',
    color: '#F59E0B',
    content: [
      'If you violate the rules:'
    ],
    penalties: [
      { offense: 'First Offense', result: 'Bet removed, Warning issued' },
      { offense: 'Repeated Offenses', result: 'Account suspended, Loss of platform privileges' },
      { offense: 'Severe Violations', result: 'Immediate permanent ban, All funds forfeited, Possible legal action' }
    ]
  },
  {
    id: 'appeals',
    icon: AlertTriangle,
    title: 'Suspension Review Process',
    color: '#8B5CF6',
    content: [
      'If your account is suspended:'
    ],
    list: [
      'You may submit an appeal request.',
      'Your case will be reviewed by our moderation panel.',
      'You may provide clarification, evidence, or corrections.'
    ],
    outcomes: [
      { icon: CheckCircle2, text: 'Suspension lifted', color: '#28A745' },
      { icon: XCircle, text: 'Suspension upheld', color: '#DC3545' }
    ],
    cta: 'Request a Review'
  },
  {
    id: 'payout',
    icon: Lock,
    title: 'Your Money is Safe',
    color: '#28A745',
    content: [
      'All bets use a secure escrow system.',
      'Funds are locked until results are confirmed.',
      'Winners are paid within: 1 minute to 1 hour.'
    ],
    list: [
      'Encrypted transactions',
      'Verified wallets',
      'Anti-fraud monitoring'
    ]
  }
];

const BettingRulesPage = () => {
  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col">
      <Header />

      {/* Hero */}
      <section className="py-16 px-4 text-center bg-gradient-to-b from-[#2D2D2D] to-[#1A1A1A]">
        <Shield className="h-16 w-16 mx-auto mb-4 text-[#D4AF37]" />
        <h1 className="text-4xl md:text-5xl font-black uppercase text-white mb-4" style={{ fontFamily: 'Outfit' }}>
          Rules, Safety & <span className="text-[#D4AF37]">Fair Play</span>
        </h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
          KrazyBets is committed to a safe, fair, and legally compliant platform. 
          All bets are reviewed. All users are protected. All winnings are paid — fast.
        </p>
      </section>

      {/* Rules Sections */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto space-y-8">
          {RULES_SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <div 
                key={section.id}
                className="card-dark"
                data-testid={`rule-section-${section.id}`}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}99 100%)` }}
                  >
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 
                      className="text-xl md:text-2xl font-bold mb-3"
                      style={{ fontFamily: 'Outfit', color: section.color }}
                    >
                      {section.title}
                    </h2>
                    
                    {section.content.map((text, idx) => (
                      <p key={idx} className="text-gray-300 mb-3">{text}</p>
                    ))}
                    
                    {section.list && (
                      <ul className="space-y-2 mb-4">
                        {section.list.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-400">
                            <span className="text-[#D4AF37] mt-1">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {section.note && (
                      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 mb-4">
                        <p className="text-sm text-[#D4AF37]">{section.note}</p>
                      </div>
                    )}
                    
                    {section.plans && (
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        {section.plans.map((plan) => (
                          <div key={plan.name} className="bg-[#2D2D2D] rounded-lg p-4 border border-[#3D3D3D]">
                            <h4 className="font-bold text-white mb-1">{plan.name}</h4>
                            <div className="text-[#D4AF37] font-bold mb-2">{plan.price}</div>
                            <ul className="space-y-1">
                              {plan.features.map((feature, idx) => (
                                <li key={idx} className="text-xs text-gray-400 flex items-start gap-1">
                                  <CheckCircle2 className="h-3 w-3 text-[#28A745] mt-0.5 flex-shrink-0" />
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {section.benefits && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {section.benefits.map((benefit, idx) => (
                          <span key={idx} className="badge-green">{benefit}</span>
                        ))}
                      </div>
                    )}
                    
                    {section.penalties && (
                      <div className="space-y-3 mb-4">
                        {section.penalties.map((penalty, idx) => (
                          <div key={idx} className="flex items-center gap-4 bg-[#2D2D2D] rounded-lg p-3">
                            <span className="text-[#D4AF37] font-bold text-sm w-32">{penalty.offense}:</span>
                            <span className="text-gray-300 text-sm">{penalty.result}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {section.outcomes && (
                      <div className="flex gap-4 mb-4">
                        {section.outcomes.map((outcome, idx) => {
                          const OutcomeIcon = outcome.icon;
                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <OutcomeIcon className="h-5 w-5" style={{ color: outcome.color }} />
                              <span className="text-gray-300">{outcome.text}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {section.cta && (
                      <Link to="/register">
                        <Button 
                          className="btn-primary text-sm"
                          style={{ background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}CC 100%)` }}
                        >
                          {section.cta}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final Statement */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="card-dark bg-gradient-to-r from-[#28A745]/10 to-[#D4AF37]/10 border-[#D4AF37] text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-[#D4AF37]" />
            <p className="text-xl text-white font-semibold mb-4">
              "KrazyBets is committed to a safe, fair, and legally compliant platform. 
              All bets are reviewed. All users are protected. All winnings are paid — fast."
            </p>
            <Link to="/register">
              <Button className="btn-primary">
                Join KrazyBets Now <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BettingRulesPage;
